import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import TableFilterBar from "../components/results/TableFilterBar";
import TableToolbar from "../components/results/TableToolbar";
import ResultsTable, { COLUMNS } from "../components/results/ResultsTable";
import ResultsList from "../components/results/ResultsList";
import ResultsGallery from "../components/results/ResultsGallery";
import Pagination from "../components/results/Pagination";
import ImageModal from "../components/results/ImageModal";
import TrademarkDetailModal from "../components/results/TrademarkDetailModal";
import AIInsights from "../components/insights/AIInsights";
import Button from "../components/ui/Button";
import { Chip } from "../components/ui/Badge";
import { Alert, EmptyState, SearchingState } from "../components/ui/Feedback";
import { useSearch } from "../app/SearchProvider";
import useResultsTable from "../hooks/useResultsTable";
import useTrademarkDetails, { mergeDetail } from "../hooks/useTrademarkDetails";
import { exportResultsToExcel } from "../lib/exportExcel";
import { formatDuration, officeLabel } from "../lib/format";

// Stable empties. `data?.results || []` would hand the table hook a brand
// new array on every render while there is no search (the error screen),
// and its effects reset state whenever `results` changes — an endless loop.
const NO_ROWS = [];
const NO_CONFLICTS = [];

/**
 * Page two: read the answer.
 *
 * Layout, top to bottom: what was searched, anything the server wants to
 * flag, the filter bar, the toolbar (view switch, columns, export), the
 * rows in one of three layouts, and pagination. The clearance panel sits in
 * a right-hand column on wide screens and drops above the table on narrow
 * ones.
 *
 * The search returns thin rows. Goods & services, owner addresses, the
 * representative and the history come from each mark's own record, which
 * is fetched only for the rows actually on screen (and the one open in the
 * detail modal) and laid over the row — see useTrademarkDetails. Filtering,
 * sorting and selection keep working on the search's own rows, so loading a
 * record never reshuffles or resets the table under the user.
 */
export default function ResultsPage() {
  const navigate = useNavigate();
  const { data, status, error, retryNotice, runSearch } = useSearch();
  const [modalImage, setModalImage] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [exportNotice, setExportNotice] = useState(null);
  const [exporting, setExporting] = useState(false);

  const results = data?.results || NO_ROWS;
  const conflicts = data?.aiInsight?.conflicts || NO_CONFLICTS;
  const table = useResultsTable(results, conflicts);
  const registry = useTrademarkDetails();
  const {
    details,
    failed,
    request: requestDetails,
    retry: retryDetail,
    getLoaded,
    isPending,
  } = registry;

  // Fetch the full record for whatever is on screen.
  useEffect(() => {
    requestDetails(table.pageRows);
  }, [table.pageRows, requestDetails]);

  const shownRows = useMemo(
    () =>
      table.pageRows.map((row) =>
        mergeDetail(row, details[row.applicationNumber]),
      ),
    [table.pageRows, details],
  );

  // The open mark is looked up by id in the filtered set, so the modal
  // always reflects the current record and Prev/Next walk the whole
  // filtered result set rather than just the current page.
  const orderedRows = table.filteredRows;
  const openIndex =
    openId === null
      ? -1
      : orderedRows.findIndex((r) => r.trademarkId === openId);
  const baseRow = openIndex >= 0 ? orderedRows[openIndex] : null;
  const openRow = baseRow
    ? mergeDetail(baseRow, details[baseRow.applicationNumber])
    : null;

  // Load the open mark, and its neighbours so stepping through is instant.
  // The open mark is always (re)tried; a neighbour the register has already
  // refused is left alone until it's actually opened, so paging past one
  // bad record doesn't re-hit the register for it on every step.
  useEffect(() => {
    if (!baseRow) return;
    const neighbours = [
      orderedRows[openIndex - 1],
      orderedRows[openIndex + 1],
    ].filter((row) => row && !failed[row.applicationNumber]);
    requestDetails([baseRow, ...neighbours]);
    // `failed` is read, not watched: a record failing must not re-trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRow, openIndex, orderedRows, requestDetails]);

  function detailStateFor(row) {
    if (!row) return "none";
    if (row.source !== "euipo") return "unsupported";
    if (!row.applicationNumber) return "none";
    if (details[row.applicationNumber]) return "loaded";
    if (failed[row.applicationNumber]) return "failed";
    return "loading";
  }

  if (status === "loading") {
    return (
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8">
        <SearchingState note={retryNotice} />
      </div>
    );
  }

  // Someone landed here without a search in session — send them to page one.
  if (status === "idle" && !data) return <Navigate to="/" replace />;

  if (status === "error") {
    return (
      <div className="mx-auto max-w-[900px] px-4 py-16 sm:px-6 lg:px-8">
        <Alert tone="error" title="The search didn't run">
          {error}
        </Alert>
        <div className="mt-5">
          <Button icon={ArrowLeft} onClick={() => navigate("/")}>
            Back to search
          </Button>
        </div>
      </div>
    );
  }

  async function handleExport(scope) {
    if (exporting) return;

    const rowsByScope = {
      selected: table.filteredRows.filter((r) =>
        table.selected.has(r.trademarkId),
      ),
      filtered: table.filteredRows,
      all: results,
    };
    const scopeLabel = {
      selected: "Rows selected in the table",
      filtered: "Rows left after the table filters",
      all: "Every row the search returned",
    };
    const baseRows = rowsByScope[scope];
    const wantsRecord = (row) =>
      row.source === "euipo" && row.applicationNumber;

    setExporting(true);
    try {
      // Rows on other pages haven't had their full record fetched yet, so
      // gather them first — otherwise the file would carry goods & services
      // for the current page and blanks for everything else.
      const missing = baseRows.filter(
        (row) => wantsRecord(row) && !getLoaded()[row.applicationNumber],
      );
      if (missing.length) {
        setExportNotice(
          `Collecting the full record for ${missing.length} ${
            missing.length === 1 ? "mark" : "marks"
          } so the file includes goods & services and owner details…`,
        );
        await requestDetails(missing);
      }

      const loaded = getLoaded();
      const rows = baseRows.map((row) =>
        mergeDetail(row, loaded[row.applicationNumber]),
      );
      const couldNotLoad = rows.filter(
        (row) => wantsRecord(row) && !row.detail,
      ).length;

      const outcome = exportResultsToExcel({
        rows,
        similarityMap: table.similarityMap,
        reasoningMap: table.reasoningMap,
        query: data.query,
        source: data.source,
        resultsCount: results.length,
        filters: scope === "all" ? null : table.filters,
        scope: scopeLabel[scope],
      });

      if (!outcome.ok) {
        setExportNotice(outcome.reason);
      } else {
        const saved = `Saved ${outcome.count} ${outcome.count === 1 ? "row" : "rows"} to ${outcome.filename}.`;
        setExportNotice(
          couldNotLoad
            ? `${saved} The register didn't return the full record for ${couldNotLoad} ${
                couldNotLoad === 1 ? "mark" : "marks"
              }, so their goods & services and owner details are blank.`
            : saved,
        );
      }
    } finally {
      setExporting(false);
      setTimeout(() => setExportNotice(null), 8000);
    }
  }

  function handleRerun() {
    runSearch(data.query).catch(() => {});
  }

  /**
   * Pivots from "does this conflict?" to "who already holds something like
   * it, and could they be approached for a license?" — a different search,
   * on a different page, with no AI conflict scoring. Only the facts that
   * actually narrow a licensing search travel over; nothing clearance-
   * specific (status, similarity, applicant) does.
   */
  function handleFindLicensing(row) {
    navigate("/potential-licensing", {
      state: {
        wordMark: row.wordMark,
        niceClasses: row.niceClasses,
        jurisdiction: row.jurisdiction,
      },
    });
  }

  const { query } = data;
  const noRowsAtAll = results.length === 0;
  const filteredToNothing = !noRowsAtAll && table.filteredRows.length === 0;
  const startIndex = (table.page - 1) * table.pageSize;

  const viewProps = {
    rows: shownRows,
    selected: table.selected,
    onToggleRow: table.toggleRow,
    similarityMap: table.similarityMap,
    onOpenImage: setModalImage,
    onOpenDetail: (row) => setOpenId(row.trademarkId),
    onFindLicensing: handleFindLicensing,
    isDetailLoading: isPending,
  };

  return (
    <div className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">
      {/* What was searched */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-600 hover:text-ink-900"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            New search
          </Link>

          <h1 className="mt-2 font-display text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
            {query?.wordMark}
          </h1>

          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <Chip>{results.length} records</Chip>
            <Chip>From {(data.source || "").toUpperCase()}</Chip>
            {data.durationMs ? (
              <Chip>{formatDuration(data.durationMs)}</Chip>
            ) : null}
            {query?.jurisdiction && (
              <Chip>{officeLabel(query.jurisdiction)}</Chip>
            )}
            {query?.niceClasses?.length > 0 && (
              <Chip>Classes {query.niceClasses.join(", ")}</Chip>
            )}
            {query?.applicantName && (
              <Chip>Applicant: {query.applicantName}</Chip>
            )}
          </div>
        </div>

        <Button variant="secondary" icon={RefreshCw} onClick={handleRerun}>
          Run this search again
        </Button>
      </div>

      {data.notices?.map((notice) => (
        <div key={notice} className="mb-4">
          <Alert tone="warning" title="About these results">
            {notice}
          </Alert>
        </div>
      ))}

      {exportNotice && (
        <div className="mb-4">
          <Alert tone="info">{exportNotice}</Alert>
        </div>
      )}

      {noRowsAtAll ? (
        <EmptyState
          title="Nothing in the register matched that"
          action={
            <Button icon={ArrowLeft} onClick={() => navigate("/")}>
              Try another name
            </Button>
          }
        >
          No record came back for this query. Try a shorter or differently
          spelled term, or widen the jurisdiction and class filters.
        </EmptyState>
      ) : (
        <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0">
            <TableFilterBar
              filters={table.filters}
              onChange={table.setFilters}
              onReset={table.resetFilters}
              options={table.options}
              activeCount={table.activeFilterCount}
              hasSimilarity={table.similarityMap.size > 0}
            />

            <TableToolbar
              columns={COLUMNS}
              visibleColumns={table.visibleColumns}
              onToggleColumn={table.toggleColumn}
              selectedCount={table.selected.size}
              filteredCount={table.filteredRows.length}
              totalCount={results.length}
              onExport={handleExport}
              onClearSelection={table.clearSelection}
              view={table.view}
              onViewChange={table.setView}
            />

            {filteredToNothing ? (
              <div className="rounded-b-soft border border-ink-200 bg-white px-6 py-16 text-center">
                <p className="font-display text-lg text-ink-900">
                  No rows match these filters
                </p>
                <p className="mx-auto mt-1.5 max-w-reading text-sm text-ink-600">
                  All {results.length} records are still loaded — clear a filter
                  to bring them back.
                </p>
                <div className="mt-5 flex justify-center">
                  <Button variant="secondary" onClick={table.resetFilters}>
                    Clear filters
                  </Button>
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-b-soft border-b border-ink-200">
                {table.view === "table" && (
                  <ResultsTable
                    {...viewProps}
                    startIndex={startIndex}
                    visibleColumns={table.visibleColumns}
                    sort={table.sort}
                    onSort={table.toggleSort}
                    onToggleAll={table.toggleAllOnPage}
                  />
                )}
                {table.view === "list" && <ResultsList {...viewProps} />}
                {table.view === "grid" && <ResultsGallery {...viewProps} />}

                <div className="border-x border-ink-200">
                  <Pagination
                    page={table.page}
                    pageSize={table.pageSize}
                    total={table.filteredRows.length}
                    onPageChange={table.setPage}
                    onPageSizeChange={table.setPageSize}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="xl:sticky xl:top-20">
            <AIInsights insight={data.aiInsight} results={results} />
          </div>
        </div>
      )}

      {modalImage && (
        <ImageModal
          imageUrl={modalImage.url}
          wordMark={modalImage.wordMark}
          onClose={() => setModalImage(null)}
        />
      )}

      {openRow && (
        <TrademarkDetailModal
          row={openRow}
          detailState={detailStateFor(openRow)}
          detailError={failed[openRow.applicationNumber]}
          onRetry={() => retryDetail(baseRow)}
          similarityScore={table.similarityMap.get(openRow.trademarkId)}
          reasoning={table.reasoningMap.get(openRow.trademarkId)}
          position={{ index: openIndex + 1, total: orderedRows.length }}
          onPrev={
            openIndex > 0
              ? () => setOpenId(orderedRows[openIndex - 1].trademarkId)
              : undefined
          }
          onNext={
            openIndex < orderedRows.length - 1
              ? () => setOpenId(orderedRows[openIndex + 1].trademarkId)
              : undefined
          }
          onClose={() => setOpenId(null)}
          onOpenImage={setModalImage}
          onFindLicensing={handleFindLicensing}
        />
      )}
    </div>
  );
}
